import localforage from "localforage";
import { defineStore } from "pinia";
import { apiClient } from "~/utils/apiClient";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import type { TreeDefinitionSummaryDto } from "~/api";

export const useTreeDefinitionStore = defineStore("TreeDefinitionStore", {
  state: (): {
    treeDefinitions: TreeDefinitionSummaryDto[];
    storeLoaded: boolean;
    isRefreshing: boolean;
    lastLoadTime: number;
  } => {
    return {
      treeDefinitions: [],
      storeLoaded: false,
      isRefreshing: false,
      lastLoadTime: 0,
    };
  },
  actions: {
    async load(forceUpdate: boolean = false) {
      const myInfoStore = useMyInfoStore();
      await myInfoStore.load();

      if (!myInfoStore.currentSysOrg?.guid) {
        return;
      }

      if (this.isRefreshing) {
        return new Promise<void>((resolve, _) => {
          const checkRefreshing = setInterval(() => {
            if (!this.isRefreshing) {
              clearInterval(checkRefreshing);
              resolve();
            }
          }, 100);
        });
      }

      this.isRefreshing = true;

      const MaxCacheTime = 10 * 60 * 1000;

      if (Date.now() - this.lastLoadTime > MaxCacheTime) {
        forceUpdate = true;
      }

      if (!forceUpdate && this.treeDefinitions.length > 0) {
        this.isRefreshing = false;
        return;
      }

      try {
        const cached = await localforage.getItem(this.$id);
        if (cached && !forceUpdate) {
          this.treeDefinitions = JSON.parse(cached as string);
          this.storeLoaded = true;
          this.isRefreshing = false;
          return;
        }

        console.log("getting fresh", this.$id);

        const list = await apiClient.tree.getAllTreeDefinitions(
          myInfoStore.currentSysOrgGuid,
        );

        this.treeDefinitions = list;
        localforage.setItem(this.$id, JSON.stringify(this.treeDefinitions));
        this.storeLoaded = true;
        this.isRefreshing = false;
        this.lastLoadTime = Date.now();
      } catch (ex) {
        this.treeDefinitions = [];
        this.storeLoaded = false;
        this.isRefreshing = false;
        throw ex;
      }
    },
    clear() {
      this.treeDefinitions = [];
      this.storeLoaded = false;
      localforage.removeItem(this.$id);
    },
    getByGuid(guid: string): TreeDefinitionSummaryDto | undefined {
      return this.treeDefinitions.find((td) => td.guid === guid);
    },
  },
});
